import React, { useEffect, useState, useRef } from "react";
import {
  Send, Bot, User, Loader2, X, MessageSquareText, Maximize2, Minimize2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectChat({ projectId, projectName }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef(null);
  const unread = useRef(0);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (projectId && open) {
      axios
        .get(`${BACKEND_URL}/api/chat/history`, { params: { project_id: projectId } })
        .then((res) => setMessages(res.data))
        .catch(() => {});
    }
  }, [projectId, open]);

  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current.querySelector("[data-radix-scroll-area-viewport]");
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [messages, sending]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);

    const tempMsg = { role: "user", content: text, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, tempMsg]);

    try {
      const res = await axios.post(`${BACKEND_URL}/api/chat`, {
        message: text,
        project_id: projectId,
      });
      setMessages((prev) => {
        const filtered = prev.filter((m) => m !== tempMsg);
        return [...filtered, res.data.user_message, res.data.assistant_message];
      });
    } catch {
      toast.error("Failed to send message");
      setMessages((prev) => prev.filter((m) => m !== tempMsg));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => { setOpen(true); setUnreadCount(0); }}
        data-testid="open-project-chat"
        className="fixed bottom-20 right-6 z-50 bg-[#003057] hover:bg-[#002545] text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-105"
        title="AI Assistant"
      >
        <MessageSquareText className="w-5 h-5" strokeWidth={1.5} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>
    );
  }

  const panelWidth = expanded ? "w-[560px]" : "w-[380px]";
  const panelHeight = expanded ? "h-[600px]" : "h-[480px]";

  return (
    <div
      className={`fixed bottom-20 right-6 z-50 ${panelWidth} ${panelHeight} bg-white border border-slate-200 rounded-sm shadow-xl flex flex-col transition-all duration-200`}
      data-testid="project-chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#003057] rounded-t-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Bot className="w-4 h-4 text-white shrink-0" strokeWidth={1.5} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">AI Assistant</p>
            <p className="text-[10px] text-white/60 truncate">{projectName}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-white/60 hover:text-white p-1 transition-colors"
            data-testid="toggle-chat-size"
          >
            {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => setOpen(false)}
            className="text-white/60 hover:text-white p-1 transition-colors"
            data-testid="close-project-chat"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full" ref={scrollRef}>
          <div className="p-3 space-y-3" data-testid="project-chat-messages">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Bot className="w-8 h-8 mx-auto text-slate-300 mb-2" strokeWidth={1.5} />
                <p className="text-xs text-slate-500 font-medium">Project Assistant</p>
                <p className="text-[10px] text-slate-400 mt-1 max-w-[240px] mx-auto">
                  Ask about your test data, analysis results, or get help writing conclusions.
                </p>
                <div className="flex flex-wrap justify-center gap-1.5 mt-3">
                  {[
                    "Summarize uploaded files",
                    "Any safety concerns?",
                    "Draft a conclusion",
                  ].map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(s)}
                      className="text-[10px] px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-full transition-colors"
                      data-testid={`project-chat-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "assistant" && (
                  <div className="w-6 h-6 rounded-sm bg-[#003057] flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
                  </div>
                )}
                <div
                  className={`max-w-[80%] px-3 py-2 text-xs leading-relaxed ${
                    msg.role === "user"
                      ? "bg-[#003057] text-white rounded-lg rounded-br-sm"
                      : "bg-slate-100 text-slate-700 rounded-lg rounded-bl-sm"
                  }`}
                >
                  {msg.role === "assistant" ? (
                    <div className="prose prose-xs prose-slate max-w-none [&_p]:text-xs [&_p]:leading-relaxed [&_li]:text-xs [&_h1]:text-sm [&_h2]:text-xs [&_h3]:text-xs [&_code]:text-[10px]">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p>{msg.content}</p>
                  )}
                </div>
                {msg.role === "user" && (
                  <div className="w-6 h-6 rounded-sm bg-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                    <User className="w-3.5 h-3.5 text-slate-600" strokeWidth={1.5} />
                  </div>
                )}
              </div>
            ))}

            {sending && (
              <div className="flex gap-2">
                <div className="w-6 h-6 rounded-sm bg-[#003057] flex items-center justify-center shrink-0">
                  <Bot className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
                </div>
                <div className="bg-slate-100 text-slate-400 rounded-lg rounded-bl-sm px-3 py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Input */}
      <div className="border-t border-slate-200 p-2.5">
        <div className="flex gap-1.5">
          <Textarea
            data-testid="project-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about this project..."
            className="rounded-sm resize-none text-xs min-h-[36px] max-h-[80px] py-2"
            rows={1}
          />
          <Button
            data-testid="project-chat-send"
            onClick={handleSend}
            disabled={!input.trim() || sending}
            className="bg-[#003057] hover:bg-[#002545] text-white rounded-sm h-[36px] w-[36px] p-0 shrink-0"
          >
            <Send className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
